import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  EncChannel,
  EncField,
  EncFieldGroup,
  EncFilterField,
  EncTopByFieldCondition,
  EncTopByFieldFilterField,
  Encoding,
  FilterType,
  VariableType,
} from '@mode-vegas/vegas';
import { ChartFacade, DragFieldInfo } from '@mode/charts/contract';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledAttr, CompiledField, Switch } from '@mode/shared/util-switch';
import { DndDropEvent } from 'ngx-drag-drop';
import { Observable, Subscription } from 'rxjs';
import { FilterModalService } from '../filter-modal/filter-modal.service';
import { DragStartData } from '../helpers/field-helpers';

interface OnDropEvent {
  event: DndDropEvent;
  channel: EncChannel;
  onFinalize?: Function;
  attr?: CompiledAttr;
}

interface DragEndData {
  event: DragEvent;
  field: EncFilterField;
}

interface FieldDropData {
  event: DndDropEvent;
  field: EncFilterField;
  channel: EncChannel;
}

@Component({
  selector: 'mode-filter-encoding',
  templateUrl: './filter-encoding.component.html',
  styleUrls: ['./filter-encoding.component.scss'],
})
export class FilterEncodingComponent implements OnInit, OnDestroy {
  @Input() encoding!: Encoding;
  @Input() observables!: {
    queryToken$: Observable<string>;
    queryRunToken$: Observable<string>;
    view$: Observable<FlamingoTypes.ModeView>;
  };
  @Input() chartService: any;
  @Input() filterModalService: FilterModalService | undefined;
  @Input() alertService: any;
  @Input() model: any;
  @Input() disableAggregate = false;
  @Input() chartType!: ChartTypes.ChartType;

  view$!: Observable<FlamingoTypes.ModeView>;
  queryToken$!: Observable<string>;

  queryToken!: string;
  queryRunToken!: string;
  view!: FlamingoTypes.ModeView;
  // legacy modal appending element selector
  appendToSelector!: string;

  subscriptions: Subscription[] = [];
  dragInfo!: DragFieldInfo;

  constructor(private chartFacade: ChartFacade) {}

  get isSystem(): boolean | undefined {
    return this.dragInfo.formula?.isSystem;
  }

  ngOnInit(): void {
    this.appendToSelector = this.chartService.readonly ? '.report-view-container' : 'body';

    this.view$ = this.observables.view$;
    this.queryToken$ = this.observables.queryToken$;

    this.subscriptions.push(
      this.chartFacade.dragField$.subscribe((dragInfo: DragFieldInfo) => (this.dragInfo = dragInfo))
    );
    this.subscriptions.push(this.observables.queryToken$.subscribe((queryToken) => (this.queryToken = queryToken)));
    this.subscriptions.push(
      this.observables.queryRunToken$.subscribe((queryRunToken) => (this.queryRunToken = queryRunToken))
    );
    this.subscriptions.push(this.observables.view$.subscribe((view: FlamingoTypes.ModeView) => (this.view = view)));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  onDragStart({ field, source }: DragStartData) {
    const isEncField = (field: EncField | EncFieldGroup): field is EncField => {
      return 'formula' in field;
    };

    if (this.view && isEncField(field)) {
      const formula = CompiledField.fromEncField(this.view, field);
      this.chartFacade.startFieldDrag(formula, source);
    }
  }

  onDragEnd({ field }: DragEndData) {
    if (this.dragInfo.formula) {
      this.removeField({ field });
      this.chartFacade.endFieldDrag();
    }
  }

  isDragging(): boolean {
    return !!this.dragInfo?.formula;
  }

  onDrop($event: OnDropEvent) {
    const { attr, onFinalize } = $event;
    const formula = attr ? attr : this.dragInfo?.formula;
    this.chartService.endDrag();
    if (formula == null) {
      return;
    }

    if (this.disableAggregate && formula.isNonScalarCalc) {
      this.alertService.error('Aggregate/Analytic filters are not supported on this visualization type');
      return;
    }

    if (!this.disableAggregate && formula.isAnalyticCalc) {
      this.alertService.error('Analytic filters are not yet supported');
      return;
    }

    const view = Switch.Views.find(this.model, this.queryToken);
    // Not very useful but also should never happen. Better than failing silently.
    if (view == null || this.filterModalService == null) {
      this.alertService.error('Unable to create filter');
      return;
    }

    this.filterModalService.addFilter(
      this.queryRunToken,
      formula,
      this.encoding,
      view,
      (filterField: EncFilterField, field: CompiledField) => {
        // Vegas' addFilter is actually upsert
        const result = EncodingHelpers.addFilterToChartEncoding(this.encoding, filterField, field);
        this.chartService.dispatchEncodingUpdate(result);

        if (onFinalize) {
          onFinalize();
        }
      },
      () => {
        if (onFinalize) {
          onFinalize();
        }
      }
    );
  }

  onFieldDrop({ event, channel }: FieldDropData) {
    // Dropping ontop of an existing pill should cause the same outcome as dropping in the empty space.
    this.onDrop({ event, channel });
  }

  editFilter({ encFilterField, oldFilter }: { encFilterField: EncFilterField; oldFilter: EncFilterField }) {
    const view = Switch.Views.find(this.model, this.queryToken);
    // Not very useful but also should never happen. Better than failing silently.
    if (view == null || this.filterModalService == null) {
      this.alertService.error('Unable to edit filter');
      return;
    }

    encFilterField = { ...encFilterField };
    encFilterField.id ??= oldFilter.id;

    this.filterModalService.editFilter(
      this.queryRunToken,
      encFilterField,
      this.encoding,
      view,
      (filterField: EncFilterField, field: CompiledField) => {
        // Vegas' addFilter is actually upsert
        const result = EncodingHelpers.addFilterToChartEncoding(this.encoding, filterField, field);
        this.chartService.dispatchEncodingUpdate(result);
      }
    );
  }

  updateVariableType($event: { field: EncFilterField; type: VariableType }) {
    let newFilterField: EncFilterField | null = null;
    let { field, type } = $event;

    if (type === VariableType.DISCRETE) {
      switch (field.filterType) {
        case FilterType.Continuous:
        case FilterType.ContinuousDate:
        case FilterType.RelativeDate: {
          newFilterField = EncodingHelpers.modifyFilterFieldContinuousToDiscrete(field);
          break;
        }
      }
    } else {
      // @todo allow top by field conversion
      const view = Switch.Views.find(this.model, this.queryToken);
      if (view != null && field.filterType === FilterType.Discrete) {
        newFilterField = EncodingHelpers.modifyFilterFieldDiscreteToContinuous(view, field, true);
      }
    }

    if (newFilterField != null) {
      this.editFilter({
        encFilterField: newFilterField,
        oldFilter: field,
      });
    }
  }

  updateSequence($event: { field: EncTopByFieldFilterField; sequence: EncTopByFieldCondition.Sequence }) {
    const { field, sequence } = $event;

    // Unchanged, bail
    if (sequence === field.cond.sequence) {
      return;
    }

    const encoder = EncodingHelpers.batchEncoding(this.encoding);
    encoder.mergeEncFilter({
      ...field,
      cond: {
        ...field.cond,
        sequence,
      },
    });

    const result = encoder.complete();
    this.chartService.dispatchEncodingUpdate(result);
  }

  removeField($event: { field: EncFilterField }) {
    const encResult = EncodingHelpers.removeFilterFromEncoding(this.encoding, $event.field);
    this.chartService.dispatchEncodingUpdate(encResult);
  }
}
